import { type FunctionComponent, useCallback, JSX } from "react";
import classnames from "classnames";

import { BetSegmentsColor, type BetSegmentsProps, BetSegmentsSize } from "./BetSegments.types";

import { SelectionSegment } from "./snowflakes/SelectionSegment/SelectionSegment.web";
import styles from "./BetSegments.web.css";

/**
 * Displays betting information in a segmented layout with support for left, middle, middle-right, and right segments.
 *
 * Presents betting details such as odds, stakes, and returns in a
 * structured four-segment layout with optional color indicators and previous value comparison.
 *
 * - [Figma](https://www.figma.com/design/an1x7EPWYyJLdAY0rBYmhF/Sports-Components?node-id=45307-52617&m=dev)
 * - [GitHub](https://github.com/Flutter-Global/the-wall/tree/main/packages/the-wall-web/components/bricks/BetSegments)
 *
 * @example
 * ```tsx
 * <BetSegments
 *   leftLabel="Odds"
 *   leftValue="2.5"
 *   midLabel="Stake"
 *   midValue="£10.00"
 *   midRightLabel="Liability"
 *   midRightValue="£5.00"
 *   rightLabel="Profit"
 *   rightValue="£12.50"
 *   size={BetSegmentsSize.LARGE}
 * />
 * ```
 */
export const BetSegments: FunctionComponent<BetSegmentsProps> = ({
  leftLabel,
  leftValue,
  leftPreviousValue,
  icon,
  isLeftSegmentOdds = true,
  isMiddleSegmentOdds = true,
  isMiddleRightSegmentOdds = true,
  isRightSegmentOdds = false,
  isOddsBoosted,
  midLabel,
  midValue,
  midValueDetail,
  midRightLabel,
  midRightValue,
  midRightRawValue,
  midRightPreviousValue,
  midRightValueDetail,
  rightLabel,
  rightValue,
  rightRawValue,
  rightPreviousValue,
  colorIndicator,
  size = BetSegmentsSize.LARGE,
}) => {
  const renderColorIndicator = useCallback((): JSX.Element => {
    const classNames = classnames(styles.dot, {
      [styles.blue]: colorIndicator === BetSegmentsColor.Blue,
      [styles.teal]: colorIndicator === BetSegmentsColor.Teal,
      [styles.pink]: colorIndicator === BetSegmentsColor.Pink,
    });
    return <span className={classNames} />;
  }, [colorIndicator]);

  return (
    <div className={styles.container}>
      {colorIndicator && renderColorIndicator()}
      <div className={styles.betSegments}>
        {leftLabel && leftValue && (
          <SelectionSegment
            term={leftLabel}
            value={leftValue}
            icon={icon}
            previousValue={leftPreviousValue}
            size={size}
            isOdds={isLeftSegmentOdds}
            className={styles.leftItem}
          />
        )}

        {midLabel && midValue && (
          <SelectionSegment
            term={midLabel}
            value={midValue}
            detail={midValueDetail}
            size={size}
            isOdds={isMiddleSegmentOdds}
            className={styles.midItem}
          />
        )}

        {midRightLabel && midRightValue && (
          <SelectionSegment
            term={midRightLabel}
            value={midRightValue}
            rawValue={midRightRawValue}
            previousValue={midRightPreviousValue}
            detail={midRightValueDetail}
            size={size}
            isOdds={isMiddleRightSegmentOdds}
            className={styles.midRightItem}
          />
        )}

        {rightLabel && rightValue && (
          <div className={styles.spacing}>
            <SelectionSegment
              term={rightLabel}
              value={rightValue}
              rawValue={rightRawValue}
              previousValue={rightPreviousValue}
              size={size}
              isOdds={isRightSegmentOdds}
              isOddsBoosted={isOddsBoosted}
              lastElement
            />
          </div>
        )}
      </div>
    </div>
  );
};
